import './App.css'
import UserDashboard from './modules/UserDashboard'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import * as React from 'react'
import { ErrorType, GetErrorTypeName, ActionType, User } from "./types.js"
import ReconnectingWebSocket from "reconnecting-websocket"
import ImperialTheme from './ImperialTheme'
import useExactUiLocaleDomTranslation from './hooks/useExactUiLocaleDomTranslation'
import { buildCachedInitialRuntimeStatus } from './utils/runtimeStatus'
import { normalizeUiLocaleLanguage } from './utils/uiLocaleCatalog'
import { mergeLogUpdate } from './utils/liveLogs'
import { createDashboardConnectionRecovery } from './utils/dashboardConnection'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, backgroundColor: '#000', color: 'red', height: '100vh' }}>
          <h1>⚠️ Something went wrong.</h1>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

const readCookieValue = key => {
  if (typeof document === 'undefined')
    return ''

  const cookiePrefix = `${key}=`
  return document.cookie
    .split('; ')
    .find(entry => entry.startsWith(cookiePrefix))
    ?.substring(cookiePrefix.length) ?? ''
}

const getAccountInfoStorageKey = sessionUuid =>
  sessionUuid ? `ggeAccountInfo:${sessionUuid}` : ''

const readCachedAccountInfo = () => {
  try {
    if (typeof window === 'undefined')
      return null

    const sessionUuid = readCookieValue('uuid')
    if (!sessionUuid)
      return null

    const rawValue = window.localStorage.getItem(getAccountInfoStorageKey(sessionUuid))
    if (!rawValue)
      return null

    const parsedValue = JSON.parse(rawValue)
    if (!parsedValue || typeof parsedValue !== 'object')
      return null

    const cachedUuid = String(parsedValue?.uuid ?? '').trim()
    if (cachedUuid && cachedUuid !== sessionUuid)
      return null

    return parsedValue
  } catch {
    return null
  }
}

const persistCachedAccountInfo = accountInfo => {
  try {
    if (typeof window === 'undefined')
      return

    const sessionUuid = readCookieValue('uuid')
    if (!sessionUuid)
      return

    const storageKey = getAccountInfoStorageKey(sessionUuid)
    if (!storageKey)
      return

    if (!accountInfo || typeof accountInfo !== 'object') {
      window.localStorage.removeItem(storageKey)
      return
    }

    window.localStorage.setItem(storageKey, JSON.stringify({
      ...accountInfo,
      uuid: String(accountInfo?.uuid ?? sessionUuid)
    }))
  } catch {
    // Ignore storage failures in restricted contexts.
  }
}

const buildWebSocketUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
  const host = window.location.host || `${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`
  return `${protocol}://${host}`
}

const USERS_REFRESH_INTERVAL_MS = 15000

function App() {
  let [users, setUsers] = React.useState([])
  let [moderationUsers, setModerationUsers] = React.useState([])
  let [usersStatus, setUsersStatus] = React.useState({})
  let [liveNow, setLiveNow] = React.useState(() => Date.now())
  let [plugins, setPlugins] = React.useState([])
  let [channelInfo, setChannelInfo] = React.useState([])
  let [logs, setLogs] = React.useState({ buffer: [], count: 0 })
  let [allianceChat, setAllianceChat] = React.useState([])
  let [isAdmin, setIsAdmin] = React.useState(false)
  let [accountInfo, setAccountInfo] = React.useState(() => readCachedAccountInfo())
  let [appLanguage, setAppLanguage] = React.useState(() => {
    try {
      return normalizeUiLocaleLanguage(window.localStorage.getItem('ggeAppLanguage') || 'en')
    } catch {
      return 'en'
    }
  })
  const statusSnapshotRef = React.useRef({})
  const pendingStatusPatchesRef = React.useRef({})
  const statusFlushTimerRef = React.useRef(null)
  const lastSocketActivityAtRef = React.useRef(Date.now())
  const connectionRecoveryRef = React.useRef(null)
  if (!connectionRecoveryRef.current) connectionRecoveryRef.current = createDashboardConnectionRecovery()
  const lastLiveNowPublishedAtRef = React.useRef(0)

  React.useEffect(() => {
    const normalizedLanguage = normalizeUiLocaleLanguage(appLanguage)

    try {
      window.localStorage.setItem('ggeAppLanguage', normalizedLanguage)
    } catch {
      // Ignore localStorage errors in restricted contexts.
    }

    document.documentElement.lang = normalizedLanguage || 'en'
  }, [appLanguage])

  const handleAppLanguageChange = React.useCallback(nextLanguage => {
    setAppLanguage(normalizeUiLocaleLanguage(nextLanguage))
  }, [])

  React.useEffect(() => {
    persistCachedAccountInfo(accountInfo)
  }, [accountInfo])

  useExactUiLocaleDomTranslation(appLanguage)

  const publishUsersStatusSnapshot = React.useCallback(nextSnapshot => {
    const normalizedSnapshot = { ...(nextSnapshot ?? {}) }
    statusSnapshotRef.current = normalizedSnapshot
    setUsersStatus(normalizedSnapshot)

    if (typeof window !== 'undefined')
      window.ggeStatus = normalizedSnapshot
  }, [])

  const mergeUsersStatusSnapshot = React.useCallback(updateSnapshot => {
    const currentSnapshot = { ...(statusSnapshotRef.current ?? {}) }
    const nextSnapshot = updateSnapshot(currentSnapshot)
    publishUsersStatusSnapshot(nextSnapshot ?? currentSnapshot)
  }, [publishUsersStatusSnapshot])

  const flushUsersStatusPatches = React.useCallback(() => {
    statusFlushTimerRef.current = null

    const pendingStatusPatches = pendingStatusPatchesRef.current
    pendingStatusPatchesRef.current = {}

    const patchEntries = Object.entries(pendingStatusPatches)
    if (patchEntries.length === 0)
      return

    const nextSnapshot = { ...(statusSnapshotRef.current ?? {}) }
    patchEntries.forEach(([userId, patch]) => {
      if (!patch || typeof patch !== 'object')
        return

      nextSnapshot[userId] = {
        ...(nextSnapshot[userId] && typeof nextSnapshot[userId] === 'object' ? nextSnapshot[userId] : {}),
        ...patch
      }
    })

    publishUsersStatusSnapshot(nextSnapshot)
  }, [publishUsersStatusSnapshot])

  const queueUsersStatusPatch = React.useCallback((statusPatch, receivedAt) => {
    if (!statusPatch || typeof statusPatch !== 'object')
      return

    const userId = Number(statusPatch.id)
    if (!Number.isFinite(userId))
      return

    const previousPatch =
      pendingStatusPatchesRef.current[userId] &&
        typeof pendingStatusPatchesRef.current[userId] === 'object' ?
        pendingStatusPatchesRef.current[userId] :
        {}

    pendingStatusPatchesRef.current[userId] = {
      ...previousPatch,
      ...statusPatch,
      lastSeenAt: Number.isFinite(Number(statusPatch?.lastSeenAt)) ? Number(statusPatch.lastSeenAt) : receivedAt,
      heartbeatAt: receivedAt
    }

    if (statusFlushTimerRef.current)
      return

    statusFlushTimerRef.current = window.setTimeout(flushUsersStatusPatches, 200)
  }, [flushUsersStatusPatches])

  const markSocketActivity = React.useCallback(receivedAt => {
    lastSocketActivityAtRef.current = receivedAt

    if (receivedAt - Number(lastLiveNowPublishedAtRef.current ?? 0) < 1000)
      return

    lastLiveNowPublishedAtRef.current = receivedAt
    setLiveNow(previousLiveNow => receivedAt > previousLiveNow ? receivedAt : previousLiveNow)
  }, [])

  React.useEffect(() => () => {
    if (statusFlushTimerRef.current)
      window.clearTimeout(statusFlushTimerRef.current)
  }, [])

  const shouldAutoRefreshUsers = React.useMemo(() => {
    const normalizedRole = String(accountInfo?.role ?? '').trim().toLowerCase()
    return Boolean(accountInfo?.uuid || isAdmin || normalizedRole === 'administrator' || normalizedRole === 'moderator')
  }, [accountInfo?.role, accountInfo?.uuid, isAdmin])

  const shouldAutoRefreshModerationUsers = React.useMemo(() => {
    const normalizedRole = String(accountInfo?.role ?? '').trim().toLowerCase()
    return Boolean(normalizedRole === 'moderator' || accountInfo?.isModerator)
  }, [accountInfo?.isModerator, accountInfo?.role])

  let ws = React.useMemo(() => {
    if (typeof window !== 'undefined') window.ggeStatus = {}
    const ws = new ReconnectingWebSocket(buildWebSocketUrl(), [], { WebSocket: WebSocket, minReconnectionDelay: 3000 })

    ws.addEventListener("message", (msg) => {
      const receivedAt = Date.now()
      markSocketActivity(receivedAt)
      let [err, action, obj] = JSON.parse(msg.data.toString())
      if (err)
        console.error(GetErrorTypeName(err))


      switch (Number(action)) {
        case ActionType.GetUUID:
          if (err === ErrorType.Unauthenticated)
            return window.location.href = "signin.html"
          break
        case ActionType.GetChannels:
          setChannelInfo(obj ?? [])
          break
        case ActionType.GetUsers:
          if (err !== ErrorType.Success)
            return

          {
            if (statusFlushTimerRef.current) {
              window.clearTimeout(statusFlushTimerRef.current)
              flushUsersStatusPatches()
            }

            const rawUsers = Array.isArray(obj?.[0]) ? obj[0] : []
            const runtimeStatusSnapshot =
              obj?.[4] && typeof obj[4] === 'object' && !Array.isArray(obj[4]) ?
                obj[4] :
                {}
            setUsers(rawUsers.map(e => new User(e)))
            mergeUsersStatusSnapshot(currentSnapshot => {
              const nextStatus = { ...currentSnapshot }
              rawUsers.forEach(user => {
                const cachedStatus = buildCachedInitialRuntimeStatus(user)
                const runtimeStatus =
                  runtimeStatusSnapshot?.[user.id] &&
                    typeof runtimeStatusSnapshot[user.id] === 'object' &&
                    !Array.isArray(runtimeStatusSnapshot[user.id]) ?
                    runtimeStatusSnapshot[user.id] :
                    {}
                const hasCachedStatus = Boolean(cachedStatus)
                const hasRuntimeStatus = Object.keys(runtimeStatus).length > 0
                const shouldResetPreviousRuntimeStatus = !hasRuntimeStatus && Number(user?.state ?? 0) === 0
                if (!cachedStatus) {
                  if (!hasRuntimeStatus && !shouldResetPreviousRuntimeStatus && !(nextStatus[user.id] && typeof nextStatus[user.id] === 'object'))
                    return
                }

                const previousUserStatus =
                  !shouldResetPreviousRuntimeStatus &&
                    nextStatus[user.id] &&
                    typeof nextStatus[user.id] === 'object' ?
                    nextStatus[user.id] :
                    {}

                nextStatus[user.id] = {
                  ...previousUserStatus,
                  ...(hasCachedStatus ? cachedStatus : {}),
                  ...(hasRuntimeStatus ? runtimeStatus : {}),
                  heartbeatAt: Math.max(
                    Number(previousUserStatus?.heartbeatAt ?? 0),
                    Number(runtimeStatus?.lastSeenAt ?? 0),
                    receivedAt
                  )
                }
              })

              return nextStatus
            })
          }
          setPlugins(obj[1])
          setIsAdmin(Boolean(obj[2]))
          setAccountInfo(previousAccountInfo => {
            const nextAccountInfo =
              obj?.[3] && typeof obj[3] === 'object' && !Array.isArray(obj[3]) ?
                obj[3] :
                null

            return nextAccountInfo ?? previousAccountInfo ?? null
          })
          break
        case ActionType.GetModerationUsers:
          if (err !== ErrorType.Success)
            return

          setModerationUsers((Array.isArray(obj) ? obj : []).map(e => new User(e)))
          break
        case ActionType.StatusUser:
          if (!obj || typeof obj !== 'object')
            return

          queueUsersStatusPatch(obj, receivedAt)

          // Debug helper: inspect in browser console via window.ggeStatus
          if (typeof window !== 'undefined') {
            console.debug('StatusUser', obj.id, {
              earnedCoins: obj.earnedCoins,
              earnedRubies: obj.earnedRubies
            })
          }
          break
        case ActionType.GetLogs:
          if (err !== ErrorType.Success)
            return
          setLogs(previousLogs => mergeLogUpdate(previousLogs, obj))
          break
        case ActionType.GetAllianceChat:
          setAllianceChat(Array.isArray(obj) ? obj : [])
          break
        default:
          return
      }
    })
    return ws
  }, [flushUsersStatusPatches, markSocketActivity, mergeUsersStatusSnapshot, queueUsersStatusPatch])

  const requestUsersRefresh = React.useCallback((force = false) => {
    if (!shouldAutoRefreshUsers)
      return false

    try {
      return connectionRecoveryRef.current.request(ws, () => {
        ws.send(JSON.stringify([ErrorType.Success, ActionType.GetUsers, {}]))
        if (shouldAutoRefreshModerationUsers)
          ws.send(JSON.stringify([ErrorType.Success, ActionType.GetModerationUsers, {}]))
      }, Date.now(), force === true)
    } catch (error) {
      console.warn('[AutoRefresh] Failed to request GetUsers', error)
      return false
    }
  }, [shouldAutoRefreshModerationUsers, shouldAutoRefreshUsers, ws])

  React.useEffect(() => {
    if (!shouldAutoRefreshUsers)
      return undefined

    requestUsersRefresh()
    const refreshInterval = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') requestUsersRefresh()
    }, USERS_REFRESH_INTERVAL_MS)

    const handleWakeRefresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden')
        return

      requestUsersRefresh()
    }

    window.addEventListener('focus', handleWakeRefresh)
    document.addEventListener('visibilitychange', handleWakeRefresh)

    return () => {
      window.clearInterval(refreshInterval)
      window.removeEventListener('focus', handleWakeRefresh)
      document.removeEventListener('visibilitychange', handleWakeRefresh)
    }
  }, [requestUsersRefresh, shouldAutoRefreshUsers])

  React.useEffect(() => {
    if (!shouldAutoRefreshUsers)
      return undefined

    const handleSocketOpen = () => {
      markSocketActivity(Date.now())
      requestUsersRefresh(true)
    }
    ws.addEventListener('open', handleSocketOpen)
    return () => ws.removeEventListener('open', handleSocketOpen)
  }, [markSocketActivity, requestUsersRefresh, shouldAutoRefreshUsers, ws])

  React.useEffect(() => {
    if (!shouldAutoRefreshUsers || typeof window === 'undefined') return undefined
    const verifyDashboardConnection = () => {
      if (document.visibilityState === 'hidden') return
      connectionRecoveryRef.current.recover(ws, lastSocketActivityAtRef.current)
    }
    const timer = window.setInterval(verifyDashboardConnection, 5000)
    window.addEventListener('online', verifyDashboardConnection)
    window.addEventListener('focus', verifyDashboardConnection)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('online', verifyDashboardConnection)
      window.removeEventListener('focus', verifyDashboardConnection)
    }
  }, [shouldAutoRefreshUsers, ws])

  React.useEffect(() => {
    if (!shouldAutoRefreshModerationUsers)
      return undefined

    try {
      ws.send(JSON.stringify([ErrorType.Success, ActionType.GetModerationUsers, {}]))
    } catch (error) {
      console.warn('[ModeratorRefresh] Failed to request GetModerationUsers', error)
    }

    return undefined
  }, [shouldAutoRefreshModerationUsers, ws])

  return (
    <div className="App">
      <ThemeProvider theme={ImperialTheme}>
        <CssBaseline />
        <ErrorBoundary>
          <UserDashboard
            isAdmin={isAdmin}
            accountInfo={accountInfo}
            liveNow={liveNow}
            ws={ws}
            plugins={plugins}
            rows={users}
            moderationUsers={moderationUsers}
            usersStatus={usersStatus}
            channelInfo={channelInfo}
            logs={logs}
            allianceChat={allianceChat}
            appLanguage={appLanguage}
            onAppLanguageChange={handleAppLanguageChange}
          />
        </ErrorBoundary>
      </ThemeProvider>
    </div>
  )
}

export default App
